import { useCallback, useEffect, useMemo, useState } from "react";
import { isAuthenticated } from "../apicalls/auth";
import { getLeagues } from "../apicalls/leagues";
import { getMatchNeeds } from "../apicalls/matchNeeds";
import "./style/MatchNeeds.css";

const normalizeRows = (rows) => Array.isArray(rows) ? rows : [];

const formatDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const ratingRange = (need) => {
  const min = need.skill_level_min;
  const max = need.skill_level_max;
  if (min && max && String(min) !== String(max)) return `${min} - ${max}`;
  return min || max || need.player_skill || "";
};

function MatchNeeds() {
  const auth = isAuthenticated();
  const token = auth?.access_token;
  const [needs, setNeeds] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [filters, setFilters] = useState({
    status: "open",
    league_id: "",
    visibility: "",
    search: "",
    date_from: "",
    date_to: "",
    include_past: "",
    limit: "100",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadNeeds = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const data = await getMatchNeeds(filters, token);
      setNeeds(normalizeRows(data.needs));
    } catch (err) {
      setError(err.message || "Failed to load match needs");
    } finally {
      setLoading(false);
    }
  }, [filters, token]);

  useEffect(() => {
    loadNeeds();
  }, [loadNeeds]);

  useEffect(() => {
    if (!token) return;
    getLeagues({ status: "" }, token)
      .then((data) => setLeagues(normalizeRows(data.leagues)))
      .catch((err) => setError(err.message || "Failed to load leagues"));
  }, [token]);

  const leagueOptions = useMemo(() => {
    const map = new Map();
    leagues.forEach((league) => map.set(String(league.id), league));
    needs.forEach((need) => {
      if (need.league_id && !map.has(String(need.league_id))) {
        map.set(String(need.league_id), {
          id: need.league_id,
          name: need.league_name || `League ${need.league_id}`,
        });
      }
    });
    return Array.from(map.values());
  }, [leagues, needs]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <main className="match-needs-page">
      <div className="match-needs-header">
        <h1>Match Needs</h1>
        <p>All league match needs created by players.</p>
      </div>

      {error ? <div className="match-needs-alert error">{error}</div> : null}

      <section className="match-needs-card">
        <div className="match-needs-filters">
          <input
            type="text"
            value={filters.search}
            onChange={(e) => setFilter("search", e.target.value)}
            placeholder="Search player, league, location"
          />
          <select value={filters.status} onChange={(e) => setFilter("status", e.target.value)}>
            <option value="open">Open</option>
            <option value="all">All statuses</option>
            <option value="cancelled">Cancelled</option>
            <option value="full">Full</option>
            <option value="completed">Completed</option>
          </select>
          <select value={filters.league_id} onChange={(e) => setFilter("league_id", e.target.value)}>
            <option value="">All leagues</option>
            {leagueOptions.map((league) => (
              <option key={league.id} value={league.id}>{league.name}</option>
            ))}
          </select>
          <select value={filters.visibility} onChange={(e) => setFilter("visibility", e.target.value)}>
            <option value="">All visibility</option>
            <option value="open">Open visibility</option>
            <option value="league">League only</option>
          </select>
          <input type="date" value={filters.date_from} onChange={(e) => setFilter("date_from", e.target.value)} />
          <input type="date" value={filters.date_to} onChange={(e) => setFilter("date_to", e.target.value)} />
          <select value={filters.include_past} onChange={(e) => setFilter("include_past", e.target.value)}>
            <option value="">Future only</option>
            <option value="true">Include past</option>
          </select>
          <select value={filters.limit} onChange={(e) => setFilter("limit", e.target.value)}>
            <option value="50">50 rows</option>
            <option value="100">100 rows</option>
            <option value="250">250 rows</option>
            <option value="500">500 rows</option>
          </select>
        </div>

        <div className="table-responsive">
          <table className="table match-needs-table">
            <thead>
              <tr>
                <th>Start</th>
                <th>Player</th>
                <th>Confirmed By</th>
                <th>League</th>
                <th>Location</th>
                <th>Rating</th>
                <th>Visibility</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8">Loading match needs...</td></tr>
              ) : needs.length ? needs.map((need) => (
                <tr key={need.id}>
                  <td>
                    <strong>{formatDateTime(need.start_date_time)}</strong>
                    <span>{need.timezone || ""}</span>
                  </td>
                  <td>
                    <strong>{need.player_name || `Player ${need.host_id}`}</strong>
                    <span>{need.player_email || need.player_phone || ""}</span>
                  </td>
                  <td>
                    <strong>{need.confirmed_by_name || (need.confirmed_by_id ? `Player ${need.confirmed_by_id}` : "-")}</strong>
                    <span>{need.confirmed_at ? formatDateTime(need.confirmed_at) : (need.confirmed_by_email || need.confirmed_by_phone || "")}</span>
                  </td>
                  <td>
                    <strong>{need.league_name || `League ${need.league_id}`}</strong>
                    <span>{[need.league_skill_band, need.league_gender].filter(Boolean).join(" / ")}</span>
                  </td>
                  <td>{need.location_text || ""}</td>
                  <td>{ratingRange(need) || "-"}</td>
                  <td><span className="match-needs-pill">{need.league_visibility || "open"}</span></td>
                  <td><span className={`match-needs-status ${need.status}`}>{need.status}</span></td>
                </tr>
              )) : (
                <tr><td colSpan="8">No match needs found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

export default MatchNeeds;
